import React, { useRef, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { confirmOrderDelivery } from '../../api/orderApi';
import LoadingAnimation from 'components/common/LoadingAnimation';
import './index.scss';
import { store } from 'react-notifications-component';
import { useHistory } from 'react-router-dom';

const OrderDeliveryConfirmation = () => {
  const { orderId } = useParams();
  const cameraRef = useRef(null);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);       
  const [isSubmitting, setIsSubmitting] = useState(false); 
  const [orderInfo, setOrderInfo] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [isAlreadyDelivered, setIsAlreadyDelivered] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({
    delivery_notes: '',
    delivery_proof_photo: null,
  });
 
  useEffect(() => {
    const fetchOrderInfo = async () => {
      try {
        setIsLoading(true);
        const response = await confirmOrderDelivery(orderId);
        if (response?.order) {
          setOrderInfo(response.order);
        }

        if (response?.order?.rider?.deliveryStatus == "delivered") {
          setIsAlreadyDelivered(true);
          store.addNotification({
            title: "Error",
            message: `This delivery has been marked delivered at ${response?.order?.rider?.deliveredAt}`,
            type: "danger",
            insert: "top",
            container: "top-right",
            dismiss: {
              duration: 5000,
              onScreen: true
            }
          });
          history.push('/browse-restaurants');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrderInfo();
  }, [orderId]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setDeliveryForm(prev => ({
      ...prev,
      delivery_proof_photo: file,
    }));

    setPreviewSrc(URL.createObjectURL(file));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDeliveryForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      if (deliveryForm.delivery_notes) {
        formData.append('delivery_notes', deliveryForm.delivery_notes);
      }
      if (deliveryForm.delivery_proof_photo) {
        formData.append('delivery_proof_photo', deliveryForm.delivery_proof_photo);
      }
      const response = await confirmOrderDelivery(orderId, formData);
      store.addNotification({
        title: 'Success!',
        message: response?.message,
        type: 'success',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: {
          duration: 5000,
          onScreen: true
        }
      });
      history.push('/browse-restaurants');
    } catch (error) {
      store.addNotification({
        title: 'Error',
        message: error.response?.data?.message,
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: {
          duration: 5000,
          onScreen: true
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <LoadingAnimation />;

  return (
    <div className="container-fluid order-delivery-confirmation">
      {isSubmitting && <LoadingAnimation />}
      <section className="mt-10 mb-4">
        <h1>Confirm your delivery</h1>
        <p className="lead">
          Optionally take a quick photo of the delivery setup and mark the order as completed.
        </p>
      </section>

      <section className="card mb-4">
        <p><strong>Order #</strong> {orderId}</p>
        <p><strong>Client:</strong> {orderInfo?.clientName ?? "-"}</p>
        <p><strong>Drop-off address:</strong> {orderInfo?.dropOffAddress ?? "-"}</p>
        {orderInfo?.type === 'GroupOrder' && (
          <p>
            <strong>People:</strong> {orderInfo?.people ?? "-"}
          </p>
        )}        
        <p><strong>Delivery time:</strong> {orderInfo?.deliveryTime ?? "-"}</p>
      </section>

      <form className="card mb-4" onSubmit={handleSubmit}>
        <div className="section-title">
          Delivery confirmation <span>(shared with Panda On Wheels team)</span>
        </div>

        <div className="field">
          <label>
            Take a delivery photo <span className="optional">(optional)</span>
          </label>

          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={handleFileChange}
          />

          <button
            type="button"
            className="btn-camera"
            onClick={() => cameraRef.current.click()}
            disabled={isAlreadyDelivered}
          >
            📸 Take Photo
          </button>

          <div className="helper">
            {previewSrc ? (
              <img src={previewSrc} alt="Delivery preview" />
            ) : (
              'No photo taken yet.'
            )}
          </div>
        </div>

        <div className="field">
          <label htmlFor="delivery_notes">Delivery notes (optional)</label>
          <textarea
            id="delivery_notes"
            name="delivery_notes"
            value={deliveryForm.delivery_notes}
            onChange={handleInputChange}
            rows="3"
            placeholder="e.g. Dropped off with reception – John confirmed receipt."
            disabled={isAlreadyDelivered}
          />
        </div>

        <div className="where-card">
          <h3>Reminder</h3>
          <ul>
            <li>Photo proof is optional but strongly recommended.</li>
            <li>
              Once submitted, the order will be marked as <strong>Delivered</strong> and Panda On Wheels will notify the client.
            </li>
          </ul>
        </div>

        <div className="actions">
          <button type="submit" className="btn-primary"
            disabled={isSubmitting || !deliveryForm.delivery_proof_photo || isAlreadyDelivered}
          >
            {isSubmitting ? 'Submitting...' : 'Mark order as delivered'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrderDeliveryConfirmation;
